import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './EmployerDashboard.css';

const EmployerDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    closedJobs: 0,
    totalViews: 0,
    totalApplications: 0,
    pendingApplications: 0
  });
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentApplications, setRecentApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch dashboard statistics
      const statsResponse = await api.get('/employer/dashboard/stats');
      if (statsResponse.data) {
        const data = statsResponse.data;
        setStats({
          totalJobs: data.jobs?.total || 0,
          activeJobs: data.jobs?.active || 0,
          closedJobs: data.jobs?.closed || 0,
          totalApplications: data.applications?.total || 0,
          pendingApplications: data.applications?.new || 0,
          totalViews: 0 // Will be calculated from jobs
        });
      }

      // Fetch recent jobs
      const jobsResponse = await api.get('/employer/jobs?limit=5');
      if (jobsResponse.data) {
        setRecentJobs(Array.isArray(jobsResponse.data) ? jobsResponse.data : []);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      const message = error?.error?.message || error?.message || 'Failed to load dashboard data';

      // Redirect employer to company profile setup if company not created
      if (typeof message === 'string' && message.toLowerCase().includes('company profile')) {
        toast.error(message, { toastId: 'employer-company-required' });
        navigate('/employer/profile');
      } else {
        toast.error(message);
      }
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge-success',
      draft: 'badge-warning',
      closed: 'badge-secondary',
      'on-hold': 'badge-warning'
    };
    return badges[status] || 'badge-secondary';
  };

  if (loading) {
    return (
      <div className="employer-dashboard">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="employer-dashboard">
      {/* Header */}
      <div className="dash-header">
        <div className="dash-header-left">
          <p className="dash-greeting">Good day 👋</p>
          <h1 className="dash-title">{user?.name || 'Employer'}</h1>
          <p className="dash-subtitle">Here's what's happening with your hiring today.</p>
        </div>
        <div className="dash-header-right">
          <Link to="/employer/post-job" className="dash-cta-btn">
            <span className="material-symbols-outlined">add</span>
            Post New Job
          </Link>
        </div>
      </div>

      {/* Statistics */}
      <div className="dash-stats">
         <div className="dash-stat-card blue">
          <div className='dash-card-top'>
          <div className="dash-stat-top">
            <div className="dash-stat-icon">
              <span className="material-symbols-outlined">work</span>
            </div>
          </div>
          <div className='dash-card-flashNo'>
           <div className="dash-stat-num">{stats.totalJobs}</div>
          </div>
          </div>

          <div className="dash-stat-label">Total Jobs</div>
          <div><p className='dash-stat-small'>Total Jobs Applied</p></div>
        </div>

        <div className="dash-stat-card green">
          <div className='dash-card-top'>
          <div className="dash-stat-top">
            <div className="dash-stat-icon">
              <span className="material-symbols-outlined">verified</span>
            </div>
          </div>
          <div className='dash-card-flashNo'>
          <div className="dash-stat-num">{stats.activeJobs}</div>
          </div>
          </div>
          <div className="dash-stat-label">Active Jobs</div>
          <div><p className='dash-stat-small'>Jobs Currently Active</p></div>
        </div>

         <div className="dash-stat-card purple">
          <div className='dash-card-top'>
          <div className="dash-stat-top">
            <div className="dash-stat-icon">
              <span className="material-symbols-outlined">group</span>
            </div>
          </div>
          <div className='dash-card-flashNo'>
          <div className="dash-stat-num">{stats.totalApplications}</div>
          </div>
          </div>
          <div className="dash-stat-label">Applications</div>
          {stats.pendingApplications > 0 && (
              <span className="dash-stat-alert">{stats.pendingApplications} new</span>
            )}
          <div><p className='dash-stat-small'>Your Submitted Applications</p></div>
        </div>

        <div className="dash-stat-card orange">
          <div className='dash-card-top'>
          <div className="dash-stat-top">
            <div className="dash-stat-icon">
              <span className="material-symbols-outlined">visibility</span>
            </div>
          </div>
          <div className='dash-card-flashNo'>
          <div className="dash-stat-num">{stats.totalViews || 0}</div>
          </div>
          </div>

          <div className="dash-stat-label">Profile Views</div>
          <div><p className='dash-stat-small'>Total Profile Views</p></div>
        </div>

      </div>

      {/* Quick Actions */}
      <div className="dash-section">
        <div className="dash-section-header">
          <h2>Quick Actions</h2>
        </div>

        <div className="dash-actions-grid">
          <Link to="/employer/post-job" className="dash-action-card accent-blue">
            <span className="material-symbols-outlined dash-action-icon-lg">search</span>
            <div>
              <h3>Post New Job</h3>
              <p>Create a new job posting</p>
            </div>
            <span className="material-symbols-outlined dash-action-arrow">arrow_forward</span>
          </Link>

          <Link to="/employer/my-jobs" className="dash-action-card accent-purple">
            <span className="material-symbols-outlined dash-action-icon-lg">work_history</span>
            <div>
              <h3>My Jobs</h3>
              <p>Manage your listings</p>
            </div>
            <span className="material-symbols-outlined dash-action-arrow">arrow_forward</span>
          </Link>

          <Link to="/employer/applications" className="dash-action-card accent-green">
            <span className="material-symbols-outlined dash-action-icon-lg">group</span>
            <div>
              <h3>Applications</h3>
              <p>Review candidates</p>
            </div>
            {stats.pendingApplications > 0 && (
              <span className="dash-notif-badge">{stats.pendingApplications}</span>
            )}
            <span className="material-symbols-outlined dash-action-arrow">arrow_forward</span>
          </Link>

          <Link to="/employer/profile" className="dash-action-card accent-orange">
            <span className="material-symbols-outlined dash-action-icon-lg">corporate_fare</span>
            <div>
              <h3>Company Profile</h3>
              <p>Update your details</p>
            </div>
            <span className="material-symbols-outlined dash-action-arrow">arrow_forward</span>
          </Link>
        </div>
      </div>

      {/* Recent Jobs */}
      {
        false &&
        <div className="recent-section">
        <div className="section-header">
          <h2>Recent Job Postings</h2>
          <Link to="/employer/my-jobs" className="view-all-link">
            View All →
          </Link>
        </div>

        {recentJobs.length === 0 ? (
          <div className="empty-state">
            <p>No jobs posted yet</p>
            <Link to="/employer/post-job" className="btn btn-primary">
              Post Your First Job
            </Link>
          </div>
        ) : (
          <div className="jobs-list">
            {recentJobs.map((job) => (
              <div key={job._id} className="job-card">
                <div className="job-card-header">
                  <div>
                    <h3>{job.title}</h3>
                    <p className="job-meta">
                      📍 {job.location?.city?.name || 'Remote'} •
                      💼 {job.jobType?.name} •
                      {job.workMode && ` 🏠 ${job.workMode}`}
                    </p>
                  </div>
                  <span className={`badge ${getStatusBadge(job.status)}`}>
                    {job.status}
                  </span>
                </div>
                <div className="job-card-stats">
                  <span>👁️ {job.views || 0} views</span>
                  <span>📝 {job.applicationsCount || 0} applications</span>
                  <span>📅 Posted {new Date(job.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="job-card-actions">
                  {/* <Link to={`/jobs/${job._id}`} className="btn btn-sm btn-outline">
                    View
                  </Link>
                  <Link to={`/employer/jobs/${job._id}/edit`} className="btn btn-sm btn-outline">
                    Edit
                  </Link> */}
                  <Link to={`/employer/jobs/${job._id}/applications`} className="btn btn-sm btn-primary">
                    Applications ({job.applicationsCount || 0})
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      }

      {/* Tips Section */}
      <div className="dash-section">
        <div className="dash-section-header">
          <h2>Tips for Success</h2>
        </div>
        <div className="dash-tips-grid">
          <div className="dash-tip-card">
            <div className="dash-tip-icon">
              <span className="material-symbols-outlined">description</span>
            </div>
            <h4>Write Clear Descriptions</h4>
            <p>Create compelling job descriptions to attract quality candidates</p>
          </div>
          <div className="dash-tip-card">
            <div className="dash-tip-icon">
              <span className="material-symbols-outlined">schedule</span>
            </div>
            <h4>Respond Promptly</h4>
            <p>Quick responses improve candidate experience and hiring speed</p>
          </div>
          <div className="dash-tip-card">
            <div className="dash-tip-icon">
              <span className="material-symbols-outlined">update</span>
            </div>
            <h4>Keep Postings Fresh</h4>
            <p>Update job postings and close filled positions regularly</p>
          </div>
          <div className="dash-tip-card">
            <div className="dash-tip-icon">
              <span className="material-symbols-outlined">manage_search</span>
            </div>
            <h4>Use Keywords</h4>
            <p>Add relevant skills and keywords to boost visibility in searches</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployerDashboard;
